import React, { useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Printable promotional card — designed for a US Letter print at 100% scale,
 * cropped to a 3.5" × 5" postcard (fits a business-card-holder pocket).
 * Public route; anyone can print. Accepts optional `?ref=CODE` so a partner
 * can bake their own affiliate code into the QR without a re-design.
 *
 * Print CSS hides everything except `.printable`, sets Letter margins to zero,
 * and forces our dark obsidian bg to render (Chrome default suppresses bg
 * colors on print).
 */
export default function PromoCard() {
  const [search] = useSearchParams();
  const ref = (search.get("ref") || "").trim();

  const url = useMemo(() => {
    const base = "https://shadowspeakjournal.com";
    return ref ? `${base}/?ref=${encodeURIComponent(ref)}` : base;
  }, [ref]);

  // Trigger the browser's native print dialog. We keep it manual (button) so
  // the user can adjust their printer settings first.
  const handlePrint = () => window.print();

  // Auto-open the dialog if the caller adds `?print=1` — useful from tooling.
  useEffect(() => {
    if (search.get("print") === "1") {
      const t = setTimeout(handlePrint, 400);
      return () => clearTimeout(t);
    }
  }, [search]);

  return (
    <div className="min-h-screen bg-obsidian-900 text-bone-100 print:bg-white">
      {/* On-screen toolbar (hidden when printing) */}
      <div className="no-print px-6 md:px-12 py-5 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to={ref ? "/partner" : "/admin"}
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
          data-testid="promo-card-back"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
        <Button
          onClick={handlePrint}
          data-testid="promo-card-print-btn"
          className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3 min-h-[44px]"
        >
          <Printer className="h-4 w-4 mr-2" /> Print card
        </Button>
      </div>

      <div className="no-print max-w-2xl mx-auto px-6 md:px-12 pt-10 pb-6 text-center">
        <p className="font-sans uppercase tracking-[0.24em] text-xs text-terracotta mb-3">
          Promo card · {ref ? `ref ${ref}` : "generic"}
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-bone-100 mb-3">
          Ready to print & hand out.
        </h1>
        <p className="font-serif italic text-bone-400">
          Load a 3.5″ × 5″ card (or any Letter stock — the crop marks will
          guide you). Preview below is 1:1.
        </p>
      </div>

      {/* The printable card itself — wrapper lets it scale on narrow screens
          without changing the 5×3.5in print geometry. */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 pb-24">
        <div className="promo-card-scale-wrap">
          <div
            className="printable mx-auto"
            data-testid="promo-card"
            style={{
              width: "5in",
              height: "3.5in",
              background: "#17161A",
              color: "#F5F0E8",
              padding: "0.35in 0.4in",
              display: "grid",
              gridTemplateColumns: "1fr 1.7in",
              gap: "0.28in",
              border: "1px solid #453D49",
              boxSizing: "border-box",
              position: "relative",
              overflow: "hidden",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
          >
          {/* subtle terracotta accent bar */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: "0.06in",
              background: "#B6A27A",
            }}
          />
          {/* Left column — copy */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <p
                style={{
                  fontFamily: "'Manrope', Arial, sans-serif",
                  fontSize: "8pt",
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                  color: "#B6A27A",
                  margin: "0 0 0.14in",
                }}
              >
                Umbra · a voice shadow journal
              </p>
              <h2
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "19pt",
                  lineHeight: 1.08,
                  fontWeight: "normal",
                  margin: "0 0 0.1in",
                  color: "#F5F0E8",
                  letterSpacing: "0.5px",
                }}
              >
                A quiet workbook
                <br />
                <em style={{ color: "#B6A27A" }}>for the parts of you</em>
                <br />
                <span style={{ fontStyle: "italic", color: "#E6E1D9" }}>
                  that were never spoken aloud.
                </span>
              </h2>
              <p
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "9.5pt",
                  lineHeight: 1.45,
                  color: "#E6E1D9",
                  margin: "0 0 0.14in",
                  fontStyle: "italic",
                }}
              >
                Seven chapters of guided shadow work — speak your answers, listen back.
              </p>
            </div>

            {/* Trial badge + URL */}
            <div>
              <div
                style={{
                  display: "inline-block",
                  border: "1px solid #B6A27A",
                  background: "rgba(182, 162, 122, 0.1)",
                  padding: "0.05in 0.14in",
                  fontFamily: "'Manrope', Arial, sans-serif",
                  fontSize: "8pt",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#B6A27A",
                  marginBottom: "0.07in",
                }}
              >
                7-day free trial · after signup
              </div>
              <p
                style={{
                  fontFamily: "'Manrope', Arial, sans-serif",
                  fontSize: "8pt",
                  letterSpacing: "0.05em",
                  color: "#F5F0E8",
                  margin: 0,
                  whiteSpace: "nowrap",
                }}
              >
                shadowspeakjournal.com
                {ref && (
                  <span style={{ color: "#B6A27A" }}>
                    {"  ·  ref "}{ref}
                  </span>
                )}
              </p>
              <p
                style={{
                  fontFamily: "'Manrope', Arial, sans-serif",
                  fontSize: "7pt",
                  letterSpacing: "0.18em",
                  color: "#B6A27A",
                  margin: "0.04in 0 0",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                Follow · @shadowspeakjournal
              </p>
            </div>
          </div>

          {/* Right column — QR code */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.1in",
            }}
          >
            <div
              style={{
                background: "#F5F0E8",
                padding: "0.1in",
                border: "1px solid #453D49",
              }}
            >
              <QRCodeSVG
                value={url}
                size={140}
                level="M"
                fgColor="#17161A"
                bgColor="#F5F0E8"
                includeMargin={false}
              />
            </div>
            <p
              style={{
                fontFamily: "'Manrope', Arial, sans-serif",
                fontSize: "7.5pt",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#B6A27A",
                margin: 0,
                textAlign: "center",
              }}
            >
              Scan to begin
            </p>
          </div>
          </div>
        </div>

        <p className="no-print mt-6 font-sans text-xs text-bone-500 text-center max-w-md mx-auto leading-relaxed">
          The QR resolves to{" "}
          <code className="text-bone-300 break-all">{url}</code>. Every new
          signup starts a 7-day trial with full workbook access, then relocks to
          the free Chapter 1 unless they pick a plan.
        </p>
      </div>

      {/* Print styles — inline so we don't wire up a global stylesheet */}
      <style>{`
        /* On-screen scaling: shrink the whole card so mobile users see it
           without horizontal scroll. The zoom property (unlike transform)
           also collapses the layout box, so the parent stops being 480px wide.
           Well supported in Blink/WebKit; Firefox added it in v126 (2024).
           Print media resets so we still print at 1:1. */
        .promo-card-scale-wrap {
          display: flex;
          justify-content: center;
        }
        @media (max-width: 520px) {
          .promo-card-scale-wrap > .printable {
            zoom: 0.72;
          }
        }
        @media (max-width: 380px) {
          .promo-card-scale-wrap > .printable {
            zoom: 0.62;
          }
        }
        @media print {
          .no-print { display: none !important; }
          @page { size: letter; margin: 0.5in; }
          body { background: #ffffff !important; }
          .promo-card-scale-wrap > .printable {
            zoom: 1 !important;
          }
          .printable {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            page-break-inside: avoid;
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
}
